import { Icon } from '@iconify/react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Check,
  ExternalLink,
  HelpCircle,
  Info,
  Loader2,
  RefreshCw,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { QueryKey } from '@/Config/api/queryKey';
import { loadFacebookSdk } from '@/Domain/components/Integration/WhatsAppPhoneNumbers/loadFacebookSdk';
import {
  AlertDialogCancel,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/Domain/components/Shared/AlertDialog/AlertDialog';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import { Label } from '@/Domain/components/Shared/Label/Label';
import { Separator } from '@/Domain/components/Shared/Separator/Separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/Domain/components/Shared/Sheet/sheet';
import { Switch } from '@/Domain/components/Shared/Switch/Switch';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import CloseIcon from '@/Domain/components/icons/Close.icon';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';
import { SCENARIO_ID } from '@/context';

interface PhoneNumberEntry {
  phone_number_id: string;
  display_phone_number: string;
  verified_name: string;
  quality_rating: string;
  is_active: boolean;
}

interface IntegrationInfo {
  whatsapp_id: string;
  account_name: string | null;
  created_timestamp: string;
  webhook_subscribed: boolean;
  admin_assigned: boolean;
  business_id: string | null;
}

interface PhoneNumbersListResponse {
  integration_info: IntegrationInfo;
  phone_numbers: PhoneNumberEntry[];
}

interface ToggleResponse {
  success: boolean;
  updated_entry: PhoneNumberEntry;
}

interface SetupStatusResponse {
  status:
    | 'not_started'
    | 'incomplete'
    | 'setup_in_progress'
    | 'setup_incomplete'
    | 'connected'
    | 'waba_inaccessible';
  has_whatsapp_id: boolean;
  has_phone_number: boolean;
  webhook_subscribed: boolean;
  admin_assigned: boolean;
  issues: string[];
  waba_id?: string | null;
}

const QUALITY_BADGE_VARIANT: Record<string, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  GREEN: 'success',
  YELLOW: 'warning',
  RED: 'destructive',
};

const QUALITY_DISPLAY_KEY: Record<string, string> = {
  GREEN: 'whatsapp_phone_numbers.card.quality_high',
  YELLOW: 'whatsapp_phone_numbers.card.quality_medium',
  RED: 'whatsapp_phone_numbers.card.quality_low',
};

const QUALITY_TOOLTIP_KEY: Record<string, string> = {
  GREEN: 'whatsapp_phone_numbers.card.quality_high_tooltip',
  YELLOW: 'whatsapp_phone_numbers.card.quality_medium_tooltip',
  RED: 'whatsapp_phone_numbers.card.quality_low_tooltip',
};

const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const WHATSAPP_DOCS_URL = 'https://docs.quickchat.ai/Channels/External%20Apps/whatsapp';
const META_REDIRECT_URI = 'https://meta.quickchat.ai/login';
const FB_APP_ID = '775209690952444';
const FB_API_VERSION = 'v18.0';
const FB_CONFIG_ID = '1503592370430764';
const CONTACT_URL = 'https://quickchat.ai/contact';

type CaptureFn = ReturnType<typeof usePostHogAnalytics>['capture'];

// FB SDK `status='unknown'` is ambiguous between "Meta refused" and "user
// closed the popup before authorising". `had_auth_response=false` plus the
// 'unknown' fallback is the popup-closed signature; distinguishing them in
// PostHog lets us tell "Meta is rejecting users" from "users are cancelling".
const classifyFbStatus = (
  had_auth_response: boolean,
  raw_status: string | undefined,
): 'connected' | 'not_authorized' | 'popup_closed_without_redirect' | 'unknown' => {
  if (had_auth_response) return 'connected';
  if (raw_status === 'not_authorized') return 'not_authorized';
  if (raw_status === 'unknown' || raw_status === undefined) {
    return 'popup_closed_without_redirect';
  }
  return 'unknown';
};

type Step1CompleteFn = (success: boolean, reasonCode?: string | null) => void;

const launchWhatsAppSignup = (
  t: (key: string) => string,
  onComplete: Step1CompleteFn,
  capture: CaptureFn,
  onAuthResponseReceived?: () => void,
): void => {
  capture(PostHogEventEnum.WhatsappSignupInitiated);

  // Pass raw JSON: URLSearchParams percent-encodes once and the backend
  // URL-decodes once. Manual encoding here would double-encode and 400.
  const state = JSON.stringify({ scenario_id: SCENARIO_ID, type: 'whatsapp' });

  window.FB.login(
    (response) => {
      const had_auth_response = !!response.authResponse;
      const raw_status = response.status;
      const status = classifyFbStatus(had_auth_response, raw_status);

      if (response.authResponse) {
        // Mark in-progress synchronously the moment Meta finishes its OAuth
        // dialog. The fetch below can take 20-30s for setup_whatsapp_account
        // to run; users who refresh during that window used to see the
        // unchanged "not started" view and assume nothing happened. Firing
        // this here, before the fetch, means the in-progress banner is up
        // by the time setup_whatsapp_account starts its first Meta call.
        onAuthResponseReceived?.();
        // Wait for the backend callback so the `whatsapp_signup_popup_returned`
        // event carries the structured `reason_code` from the funnel
        // (no_phone_number_on_waba, no_whatsapp_business_id, etc.). Firing it
        // before the fetch resolves was the old behaviour and gave us nothing
        // but `status=unknown` rows in PostHog.
        const params = new URLSearchParams({
          code: response.authResponse.code,
          state,
        });
        fetch(`${META_REDIRECT_URI}?${params.toString()}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        })
          .then((r) => {
            const backend_status = r.headers.get('X-Quickchat-Integration-Status') ?? null;
            const reason_code = r.headers.get('X-Quickchat-Integration-Reason-Code') ?? null;
            capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
              had_auth_response,
              status,
              raw_fb_status: raw_status ?? null,
              backend_status,
              reason_code,
              http_status: r.status,
            });
            capture(PostHogEventEnum.WhatsappSignupCallbackReceived, {
              status: r.status,
              reason_code,
            });
            // r.ok alone is the wrong success signal: backend always returns
            // 200 with the HTML callback page. Treat backend_status='success'
            // as the real success criterion.
            const succeeded = r.ok && backend_status === 'success';
            if (!succeeded) {
              capture(PostHogEventEnum.WhatsappSignupCallbackFailed, {
                reason: r.ok ? 'backend_error' : 'http_error',
                status: r.status,
                reason_code,
                backend_status,
              });
            }
            onComplete(succeeded, reason_code);
          })
          .catch((error) => {
            console.error('Error:', error);
            capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
              had_auth_response,
              status,
              raw_fb_status: raw_status ?? null,
              backend_status: null,
              reason_code: 'fetch_threw',
            });
            capture(PostHogEventEnum.WhatsappSignupCallbackFailed, { reason: 'fetch_threw' });
            onComplete(false, 'fetch_threw');
          });
      } else {
        capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
          had_auth_response,
          status,
          raw_fb_status: raw_status ?? null,
          backend_status: null,
          reason_code: null,
        });
        onComplete(false, null);
      }
    },
    {
      config_id: FB_CONFIG_ID,
      response_type: 'code',
      override_default_response_type: true,
      // `reauthorize` (forces re-login), not `rerequest` (re-prompts for
      // declined permissions). Earlier attempt to switch this to `rerequest`
      // to unlock `business_management` was based on a "cached scopes"
      // hypothesis that was falsified by Dominik's logout/re-login test:
      // a fresh Facebook login produced the same partial scope grant. The
      // missing scope is a Meta-side variation behaviour, not a flag bug.
      // Do not change this back without re-reading the PR diagnosis.
      auth_type: 'reauthorize',
    },
  );
};

const connectWhatsAppNumber = (): Window | null => {
  // URLSearchParams URL-encodes each value once. Hand-concatenating produced
  // a redirect_uri with literal `/`s that Meta's dialog parsed as the bare
  // origin, sending users to https://meta.quickchat.ai/#_=_ instead of /login.
  const params = new URLSearchParams({
    response_type: 'code',
    display: 'popup',
    client_id: FB_APP_ID,
    redirect_uri: META_REDIRECT_URI,
    auth_type: 'rerequest',
    scope: [
      'email',
      'whatsapp_business_management',
      'whatsapp_business_messaging',
      'business_management',
    ].join(','),
    state: JSON.stringify({ scenario_id: SCENARIO_ID, type: 'whatsapp' }),
  });

  return window.open(
    `https://www.facebook.com/v18.0/dialog/oauth?${params.toString()}`,
    '_blank',
    'menubar=no,location=no,resizable=no,scrollbars=no,status=no,height=800,width=600',
  );
};

export const WHATSAPP_PHONE_NUMBERS_SHEET = MODAL_NAMES.WHATSAPP_PHONE_NUMBERS_SHEET;

const StepIndicator = ({ completed, stepNumber }: { completed: boolean; stepNumber: number }) => {
  if (completed) {
    return (
      <div className="flex items-center justify-center size-7 rounded-full bg-green-500 text-white shrink-0">
        <Check className="size-4" />
      </div>
    );
  }
  return (
    <div className="flex items-center justify-center size-7 rounded-full border-2 border-border text-foreground-subtle text-sm font-semibold shrink-0">
      {stepNumber}
    </div>
  );
};

const IssuesBanner = ({ issues }: { issues: string[] }) => {
  if (issues.length === 0) return null;
  return (
    <div
      className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-1.5"
      data-testid="issues-banner"
    >
      <div className="flex items-center gap-1.5">
        <AlertTriangle className="size-4 text-amber-600 shrink-0" />
        <span className="text-sm font-medium text-amber-800">
          {issues.length === 1 ? '1 issue detected' : `${issues.length} issues detected`}
        </span>
      </div>
      <ul className="space-y-1 ml-5.5">
        {issues.map((issue) => (
          <li key={issue} className="text-xs text-amber-700 list-disc">
            {issue}
          </li>
        ))}
      </ul>
    </div>
  );
};

// How long the in-progress banner can stay up before we give up waiting for
// `integrationStatus === 'connected'`. setup_whatsapp_account observed
// taking ~22s in Production (Datadog trace c7953edcb53c14cc57251c2d360776be);
// 60s gives a comfortable margin without parking a broken state on screen.
const SETUP_IN_PROGRESS_TIMEOUT_MS = 60_000;
// Status query refetch cadence while a setup attempt is in flight. Belt-and-
// suspenders for the COOP-may-opaque-popup.closed and missing-postMessage
// cases; the user gets a sheet update within ~3s of the backend writing the
// row even if both existing refresh triggers fail silently.
const SETUP_IN_PROGRESS_POLL_INTERVAL_MS = 3_000;
// Hard cap on Step 2 popup-tied status polling so a COOP-opaque popup that
// never reports closed doesn't poll forever. Generous enough to cover the
// full embed-completion window (typically 60s) plus backend setup (10s).
const STEP2_POPUP_POLL_TIMEOUT_MS = 120_000;
// Auto-expire window for the persistent "Step 1 succeeded" flag.
const STEP1_ATTEMPTED_TTL_MS = 24 * 60 * 60 * 1_000;
// Scoped per scenario — an unscoped key leaked Step 1 "complete" state into
// every other AI Agent opened in the same browser, including brand-new ones.
const STEP1_ATTEMPTED_LS_KEY = `whatsapp_step1_attempted:${SCENARIO_ID}`;

const readStep1AttemptedFromStorage = (): number | null => {
  try {
    const raw = window.localStorage.getItem(STEP1_ATTEMPTED_LS_KEY);
    if (!raw) return null;
    const since = Number(raw);
    if (!Number.isFinite(since)) return null;
    if (Date.now() - since > STEP1_ATTEMPTED_TTL_MS) return null;
    return since;
  } catch {
    return null;
  }
};

const writeStep1AttemptedToStorage = (since: number | null): void => {
  try {
    if (since === null) {
      window.localStorage.removeItem(STEP1_ATTEMPTED_LS_KEY);
    } else {
      window.localStorage.setItem(STEP1_ATTEMPTED_LS_KEY, String(since));
    }
  } catch {
    // localStorage can throw in private-browsing modes
  }
};

interface SetupInProgressFlag {
  since: number | null;
}

// localStorage key for the in-progress flag. Survives full page reload —
// without persistence, a user who refreshes during the 20-30s setup window
// loses the in-progress banner and sees an unchanged "not started" view
// (Dominik's "Step 1 changed nothing even after refresh" symptom).
const SETUP_IN_PROGRESS_LS_KEY = 'whatsapp_setup_in_progress';

const readSetupInProgressFromStorage = (): SetupInProgressFlag => {
  try {
    const raw = window.localStorage.getItem(SETUP_IN_PROGRESS_LS_KEY);
    if (!raw) return { since: null };
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null || !('since' in parsed)) {
      return { since: null };
    }
    const since = (parsed as { since: unknown }).since;
    if (typeof since !== 'number') return { since: null };
    // Auto-expire stale flags so a tab that crashed mid-setup doesn't park
    // the banner forever on the next visit. The 60s window mirrors the
    // backend's SETUP_IN_PROGRESS_WINDOW.
    if (Date.now() - since > SETUP_IN_PROGRESS_TIMEOUT_MS) return { since: null };
    return { since };
  } catch {
    return { since: null };
  }
};

const writeSetupInProgressToStorage = (flag: SetupInProgressFlag): void => {
  try {
    if (flag.since === null) {
      window.localStorage.removeItem(SETUP_IN_PROGRESS_LS_KEY);
    } else {
      window.localStorage.setItem(SETUP_IN_PROGRESS_LS_KEY, JSON.stringify(flag));
    }
  } catch {
    // localStorage can throw in private-browsing modes; the in-memory React
    // Query cache still works, we just lose persistence across reload.
  }
};

export const WhatsAppPhoneNumbersManager = () => {
  const { t } = useTranslation(['integrations']);
  const open = useModalStore(
    useCallback((state) => state.isOpen(WHATSAPP_PHONE_NUMBERS_SHEET), []),
  );
  const closeModal = useModalStore((state) => state.closeModal);
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  const [disconnectDialogOpen, setDisconnectDialogOpen] = useState(false);
  const [step2Popup, setStep2Popup] = useState<Window | null>(null);
  const [step2PollingUntil, setStep2PollingUntil] = useState<number | null>(null);
  // Ref, not state: the 500ms popup-close interval reads the latest value across renders, distinguishing a clean result-posting close from a silent COOP/cancel close.
  const step2PostMessageReceivedRef = useRef(false);

  // Client cache slot — backed by localStorage so the in-progress banner
  // survives a full page reload during the 20-30s setup window. A Step 1
  // / Step 2 callback flips this to a timestamp; the sheet renders the
  // in-progress banner and disables both setup buttons until either
  // (a) `integrationStatus` transitions to 'connected', or (b) the local
  // SETUP_IN_PROGRESS_TIMEOUT_MS elapses, or (c) the backend status query
  // confirms `setup_in_progress` is no longer the right status.
  const { data: setupInProgress } = useQuery<SetupInProgressFlag>({
    queryKey: [QueryKey.whatsappPhoneNumbers.setupInProgress],
    queryFn: () => Promise.resolve(readSetupInProgressFromStorage()),
    staleTime: Infinity,
    initialData: readSetupInProgressFromStorage,
  });
  const setupInProgressSince = setupInProgress?.since ?? null;
  const integrationStatusFromBackend = useQuery<SetupStatusResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.status],
    enabled: false, // just a read of the cache; the main query below is what fetches
  }).data?.status;
  // Show the in-progress banner when EITHER the local flag is set (fresh
  // click feedback before any backend roundtrip) OR the backend status
  // reports setup_in_progress (covers the page-reload case where the local
  // flag may have been cleared but the row was created < 60s ago on the
  // server side).
  const isSetupInProgress =
    setupInProgressSince !== null || integrationStatusFromBackend === 'setup_in_progress';

  const markSetupInProgress = useCallback(() => {
    const flag = { since: Date.now() };
    writeSetupInProgressToStorage(flag);
    queryClient.setQueryData<SetupInProgressFlag>(
      [QueryKey.whatsappPhoneNumbers.setupInProgress],
      flag,
    );
  }, [queryClient]);

  const clearSetupInProgress = useCallback(() => {
    const flag: SetupInProgressFlag = { since: null };
    writeSetupInProgressToStorage(flag);
    queryClient.setQueryData<SetupInProgressFlag>(
      [QueryKey.whatsappPhoneNumbers.setupInProgress],
      flag,
    );
  }, [queryClient]);

  const { data: step1Attempted } = useQuery<number | null>({
    queryKey: [QueryKey.whatsappPhoneNumbers.step1Attempted],
    queryFn: () => Promise.resolve(readStep1AttemptedFromStorage()),
    staleTime: Infinity,
    initialData: readStep1AttemptedFromStorage,
  });

  const markStep1Attempted = useCallback(() => {
    const ts = Date.now();
    writeStep1AttemptedToStorage(ts);
    queryClient.setQueryData<number | null>([QueryKey.whatsappPhoneNumbers.step1Attempted], ts);
  }, [queryClient]);

  const clearStep1Attempted = useCallback(() => {
    writeStep1AttemptedToStorage(null);
    queryClient.setQueryData<number | null>([QueryKey.whatsappPhoneNumbers.step1Attempted], null);
  }, [queryClient]);

  const {
    data: statusData,
    isLoading: statusLoading,
    isFetching: statusFetching,
  } = useQuery<SetupStatusResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.status],
    enabled: open,
  });

  const integrationStatus = statusData?.status ?? 'not_started';

  const { data, isLoading } = useQuery<PhoneNumbersListResponse>({
    queryKey: [QueryKey.whatsappPhoneNumbers.list],
    enabled: open && integrationStatus === 'connected',
  });

  const toggleMutation = useMutation<
    ToggleResponse,
    Error,
    { phone_number_id: string; is_active: boolean }
  >({
    mutationKey: [QueryKey.whatsappPhoneNumbers.toggle],
  });

  const deleteMutation = useMutation<{ success: boolean }, Error, void>({
    mutationKey: [QueryKey.whatsappPhoneNumbers.delete],
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.list] });
      void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.status] });
      clearStep1Attempted();
      clearSetupInProgress();
      toast.success(t('whatsapp_phone_numbers.disconnect.success'));
      setDisconnectDialogOpen(false);
      closeModal();
    },
    onError: () => {
      toast.error(t('whatsapp_phone_numbers.disconnect.error'));
    },
  });

  const phoneNumbers = data?.phone_numbers ?? [];
  const integrationInfo = data?.integration_info;

  const handleToggle = (entry: PhoneNumberEntry, checked: boolean) => {
    toggleMutation.mutate(
      {
        phone_number_id: entry.phone_number_id,
        is_active: checked,
      },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.list] });
          void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.status] });
          if (checked) {
            toast.success(
              t('whatsapp_phone_numbers.toast.activated', { number: entry.display_phone_number }),
            );
          } else {
            toast.success(
              t('whatsapp_phone_numbers.toast.deactivated', {
                number: entry.display_phone_number,
              }),
            );
          }
        },
        onError: () => {
          toast.error(t('whatsapp_phone_numbers.toast.error'));
        },
      },
    );
  };

  const handleRefreshStatus = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.status] });
    void queryClient.invalidateQueries({ queryKey: [QueryKey.whatsappPhoneNumbers.list] });
  }, [queryClient]);

  // Reason-code → user-facing Toast. Each backend setup failure surfaces a
  // specific reason_code via the OAuth callback HTML; we map it to copy
  // that tells the user what to do next, instead of the generic
  // "transient — please retry" Toast that hid every distinct failure
  // mode into one bucket (the bug that compounded PR #4015 → #4033).
  const dispatchToastForReasonCode = useCallback(
    (reasonCode: string | null | undefined) => {
      if (!reasonCode) return;
      switch (reasonCode) {
        case 'missing_business_management_scope':
          toast.error(t('whatsapp_phone_numbers.toast.missing_business_management.title'), {
            description: t('whatsapp_phone_numbers.toast.missing_business_management.description'),
            duration: 12000,
          });
          break;
        case 'admin_assign_failed_not_business_admin':
          toast.error(t('whatsapp_phone_numbers.toast.not_business_admin.title'), {
            description: t('whatsapp_phone_numbers.toast.not_business_admin.description'),
            duration: 12000,
          });
          break;
        case 'webhook_subscribe_failed':
          toast.error(t('whatsapp_phone_numbers.toast.webhook_subscribe_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.webhook_subscribe_failed.description'),
            duration: 12000,
          });
          break;
        case 'phone_register_failed':
          toast.error(t('whatsapp_phone_numbers.toast.phone_register_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.phone_register_failed.description'),
            duration: 12000,
          });
          break;
        case 'oauth_code_already_used':
        case 'oauth_exchange_failed':
          toast.error(t('whatsapp_phone_numbers.toast.oauth_exchange_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.oauth_exchange_failed.description'),
            duration: 10000,
          });
          break;
        case 'no_whatsapp_business_id':
          toast.error(t('whatsapp_phone_numbers.toast.no_whatsapp_business_id.title'), {
            description: t('whatsapp_phone_numbers.toast.no_whatsapp_business_id.description'),
            duration: 15000,
          });
          break;
        case 'no_phone_number_on_waba':
          toast.error(t('whatsapp_phone_numbers.toast.no_phone_number_on_waba.title'), {
            description: t('whatsapp_phone_numbers.toast.no_phone_number_on_waba.description'),
            duration: 20000,
          });
          break;
        case 'multiple_businesses_selected':
          toast.error(t('whatsapp_phone_numbers.toast.multiple_businesses_selected.title'), {
            description: t('whatsapp_phone_numbers.toast.multiple_businesses_selected.description'),
            duration: 15000,
          });
          break;
        case 'save_integrity_error':
          toast.error(t('whatsapp_phone_numbers.toast.save_integrity_error.title'), {
            description: t('whatsapp_phone_numbers.toast.save_integrity_error.description'),
            duration: 15000,
          });
          break;
        case 'waba_not_shared_with_partner':
          toast.error(t('whatsapp_phone_numbers.toast.waba_not_shared_with_partner.title'), {
            description: t('whatsapp_phone_numbers.toast.waba_not_shared_with_partner.description'),
            duration: 20000,
          });
          break;
        case 'setup_failed':
          toast.error(t('whatsapp_phone_numbers.toast.setup_failed.title'), {
            description: t('whatsapp_phone_numbers.toast.setup_failed.description'),
            duration: 12000,
          });
          break;
        default:
          // Unmapped reason_code — surface the drift instead of silently bucketing it into the generic toast.
          console.error('[WhatsApp] Unmapped connect-failure reason_code:', reasonCode);
          toast.error(t('whatsapp_phone_numbers.toast.generic.title'), {
            description: t('whatsapp_phone_numbers.toast.generic.description'),
            duration: 12000,
          });
          break;
      }
    },
    [t],
  );

  const handleStep1Complete = useCallback(
    (success: boolean, reasonCode: string | null | undefined) => {
      // Always invalidate the status query regardless of success — without
      // this, Dominik's "Refresh button did nothing" symptom recurs because
      // the cached useQuery data wouldn't reflect the just-completed (or
      // just-failed) OAuth round-trip until the next manual reload.
      handleRefreshStatus();
      if (success) {
        // Backend returned 200 + X-Quickchat-Integration-Status=success. This
        // covers BOTH the fresh-OAuth happy path AND the idempotent 36009
        // replay (reason_code in {connected, reconnected, setup_in_progress}).
        // Show the in-progress banner until the status query confirms
        // 'connected'.
        markStep1Attempted();
        markSetupInProgress();
        toast.info(t('whatsapp_phone_numbers.setup.checking_status'));
      } else {
        clearSetupInProgress();
        dispatchToastForReasonCode(reasonCode);
      }
    },
    [
      t,
      handleRefreshStatus,
      dispatchToastForReasonCode,
      markStep1Attempted,
      markSetupInProgress,
      clearSetupInProgress,
    ],
  );

  const handleStep1Click = useCallback(() => {
    // The SPA never loads the Facebook JS SDK globally (the old server-rendered
    // template did), so it must be loaded on demand before FB.login exists.
    loadFacebookSdk(FB_APP_ID, FB_API_VERSION)
      .then(() => launchWhatsAppSignup(t, handleStep1Complete, capture, markSetupInProgress))
      .catch(() => {
        capture(PostHogEventEnum.WhatsappSignupSdkLoadFailed);
        toast.error(t('whatsapp_phone_numbers.toast.sdk_load_failed.title'), {
          description: t('whatsapp_phone_numbers.toast.sdk_load_failed.description'),
          duration: 12000,
        });
      });
  }, [t, handleStep1Complete, capture, markSetupInProgress]);

  const handleStep2Click = useCallback(() => {
    // Don't mark in-progress here — the Meta embed can take 30-60s; setupInProgress is set in the postMessage('success') handler.
    capture(PostHogEventEnum.WhatsappStep2PopupOpened);
    step2PostMessageReceivedRef.current = false;
    const popup = connectWhatsAppNumber();
    setStep2Popup(popup);
    setStep2PollingUntil(Date.now() + STEP2_POPUP_POLL_TIMEOUT_MS);
  }, [capture]);

  // Status polling while a setup attempt is in flight. We already invalidate
  // on Step 1 callback, on Step 2 popup close, and on Step 2 postMessage —
  // this is the safety net for when all three miss (e.g. COOP making
  // popup.closed opaque for a cross-origin popup).
  useEffect(() => {
    if (!isSetupInProgress) return;
    const interval = setInterval(handleRefreshStatus, SETUP_IN_PROGRESS_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [isSetupInProgress, handleRefreshStatus]);

  // Auto-clear the in-progress flag after a hard timeout so a backend that
  // silently dies doesn't park the banner on screen forever.
  useEffect(() => {
    if (!setupInProgressSince) return;
    const elapsed = Date.now() - setupInProgressSince;
    const remaining = Math.max(0, SETUP_IN_PROGRESS_TIMEOUT_MS - elapsed);
    const timeout = setTimeout(clearSetupInProgress, remaining);
    return () => clearTimeout(timeout);
  }, [setupInProgressSince, clearSetupInProgress]);

  // Clear the in-progress flag once the status query confirms connected —
  // the integration is live, the user no longer needs the loading banner.
  useEffect(() => {
    if (integrationStatus === 'connected' && isSetupInProgress) {
      clearSetupInProgress();
    }
  }, [integrationStatus, isSetupInProgress, clearSetupInProgress]);

  // Fire once per real non-connected → connected transition. The undefined guard keeps the loading-state 'not_started' fallback from seeding the ref, else a cold load straight to connected logs a false completion.
  const priorIntegrationStatusRef = useRef<SetupStatusResponse['status'] | null>(null);
  useEffect(() => {
    if (statusData === undefined) return;
    const prior = priorIntegrationStatusRef.current;
    if (prior && prior !== 'connected' && integrationStatus === 'connected') {
      capture(PostHogEventEnum.WhatsappConnectCompleted, { prior_status: prior });
    }
    priorIntegrationStatusRef.current = integrationStatus;
  }, [statusData, integrationStatus, capture]);

  useEffect(() => {
    if (integrationStatus === 'connected' && step1Attempted !== null) {
      clearStep1Attempted();
    }
  }, [integrationStatus, step1Attempted, clearStep1Attempted]);

  useEffect(() => {
    if (integrationStatus === 'connected' && step2PollingUntil !== null) {
      setStep2PollingUntil(null);
    }
  }, [integrationStatus, step2PollingUntil]);

  // Poll for Step 2 popup close
  useEffect(() => {
    if (!step2Popup) return;

    const interval = setInterval(() => {
      if (step2Popup.closed) {
        clearInterval(interval);
        if (!step2PostMessageReceivedRef.current) {
          // Popup closed without ever posting `whatsapp_connection_result` —
          // either the user dismissed the Meta dialog or COOP blocked the
          // postMessage. Either way, the funnel ends here without a structured
          // result; capture it explicitly so the dashboards can size this
          // dark-funnel cohort instead of inferring it from missing events.
          capture(PostHogEventEnum.WhatsappStep2PopupClosedWithoutMessage);
        }
        setStep2Popup(null);
        toast.info(t('whatsapp_phone_numbers.setup.checking_status'));
        handleRefreshStatus();
      }
    }, 500);

    return () => clearInterval(interval);
  }, [step2Popup, t, handleRefreshStatus, capture]);

  // COOP-resistant status polling, independent of step2Popup. The popup.closed
  // detection above can fire false-positives under COOP and null step2Popup
  // mid-flow; we key on a dedicated timestamp so polling survives that.
  useEffect(() => {
    if (step2PollingUntil === null) return;
    const remaining = step2PollingUntil - Date.now();
    if (remaining <= 0) {
      setStep2PollingUntil(null);
      return;
    }
    const interval = setInterval(handleRefreshStatus, SETUP_IN_PROGRESS_POLL_INTERVAL_MS);
    const stop = setTimeout(() => {
      clearInterval(interval);
      setStep2PollingUntil(null);
    }, remaining);
    return () => {
      clearInterval(interval);
      clearTimeout(stop);
    };
  }, [step2PollingUntil, handleRefreshStatus]);

  // Listen for postMessage from OAuth popup. The popup (Step 2's
  // window.open + Meta OAuth redirect) renders `meta_callback.html`, which
  // posts a `whatsapp_connection_result` message carrying `status` and the
  // backend-emitted `reason_code`. Forward both to the
  // `whatsapp_signup_popup_returned` PostHog event so dashboards stop
  // collapsing everything into `status=unknown`.
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data?.type !== 'whatsapp_connection_result') return;

      const { status, title, message, reason_code } = event.data as {
        status: string;
        title: string;
        message: string;
        reason_code?: string;
      };

      // Flag that the Step 2 popup posted back a structured result, so the
      // popup-close watcher above doesn't double-count this as a silent close.
      step2PostMessageReceivedRef.current = true;

      capture(PostHogEventEnum.WhatsappSignupPopupReturned, {
        had_auth_response: true,
        status: status === 'success' ? 'connected' : 'backend_error',
        raw_fb_status: null,
        backend_status: status,
        reason_code: reason_code ?? null,
        source: 'popup_postmessage',
      });

      if (status === 'success') {
        // Same in-progress handling as Step 1: covers fresh success,
        // 36009-replay-reconnected, and 36009-replay-setup-in-progress.
        markSetupInProgress();
        toast.success(title);
      } else if (reason_code) {
        // Prefer the structured reason_code mapping over the inline HTML
        // message; same dispatcher Step 1 uses, so the two paths render
        // identical copy for the same failure mode.
        dispatchToastForReasonCode(reason_code);
      } else {
        const plainMessage = message
          .replace(/<[^>]*>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        toast.error(title, { description: plainMessage, duration: 10000 });
      }
      handleRefreshStatus();
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleRefreshStatus, capture, dispatchToastForReasonCode, markSetupInProgress]);

  const handleClose = () => {
    setDisconnectDialogOpen(false);
    closeModal();
  };

  const step1Complete =
    integrationStatus === 'incomplete' ||
    integrationStatus === 'connected' ||
    step1Attempted !== null;
  const step2Complete = integrationStatus === 'connected';

  // Failed-state removals never went live, so the connected-state disconnect copy ("stop receiving messages") is wrong.
  const isStartOverDisconnect =
    integrationStatus === 'setup_incomplete' || integrationStatus === 'waba_inaccessible';
  const disconnectDialogCopy = isStartOverDisconnect
    ? {
        title: t('whatsapp_phone_numbers.disconnect.start_over_title'),
        description: t('whatsapp_phone_numbers.disconnect.start_over_description'),
        confirm: t('whatsapp_phone_numbers.disconnect.start_over_confirm'),
      }
    : {
        title: t('whatsapp_phone_numbers.disconnect.title'),
        description: t('whatsapp_phone_numbers.disconnect.description'),
        confirm: t('whatsapp_phone_numbers.disconnect.confirm'),
      };

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        className="max-w-3xl w-full h-full"
        tabIndex={undefined}
        data-testid="whatsapp-phone-numbers-sheet"
      >
        <SheetHeader className="px-0.5">
          <SheetTitle>{t('whatsapp_phone_numbers.title')}</SheetTitle>
          <section className="flex items-center gap-2">
            <IconButton onClick={handleClose} variant="secondary" data-testid="close-button">
              <CloseIcon />
            </IconButton>
          </section>
        </SheetHeader>

        <div className="overflow-y-auto px-0.5 mt-4 space-y-6">
          <p className="text-sm text-muted-foreground">
            {t('whatsapp_phone_numbers.setup.description')}{' '}
            <a
              href={WHATSAPP_DOCS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-medium hover:text-foreground"
              data-testid="whatsapp-setup-docs-link"
            >
              {t('whatsapp_phone_numbers.setup.docs_link')}
              <ExternalLink className="inline size-3 ml-0.5 -mt-0.5" />
            </a>
          </p>

          {statusLoading ? (
            <div className="space-y-4" data-testid="setup-skeleton">
              <div className="rounded-xl bg-muted animate-pulse h-16" />
              <div className="rounded-xl bg-muted animate-pulse h-16" />
              <div className="rounded-xl bg-muted animate-pulse h-14" />
              <div className="rounded-xl bg-muted animate-pulse h-14" />
              <div className="rounded-lg bg-muted animate-pulse w-32 h-8" />
            </div>
          ) : (
            <div className="space-y-5" data-testid="whatsapp-setup-view">
              <p className="text-sm text-muted-foreground">
                {t('whatsapp_phone_numbers.setup.body')}
              </p>

              <div className="rounded-xl border border-border bg-muted p-3">
                <div className="flex items-start gap-2">
                  <Info className="size-4 text-foreground-subtle shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('whatsapp_phone_numbers.setup.phone_required_title')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('whatsapp_phone_numbers.setup.phone_required_description')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-border bg-muted p-3">
                <div className="flex items-start gap-2">
                  <Info className="size-4 text-foreground-subtle shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-muted-foreground">
                      {t('whatsapp_phone_numbers.setup.meta_onboarding_title')}
                    </p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {t('whatsapp_phone_numbers.setup.meta_onboarding_description')}
                    </p>
                  </div>
                </div>
              </div>

              {integrationStatus === 'waba_inaccessible' && (
                <div
                  className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2"
                  data-testid="waba-inaccessible-banner"
                >
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className="size-4 text-amber-600 shrink-0" />
                    <span className="text-sm font-medium text-amber-800">
                      {t('whatsapp_phone_numbers.setup.waba_inaccessible_title')}
                    </span>
                  </div>
                  <p className="text-xs text-amber-800">
                    {t('whatsapp_phone_numbers.setup.waba_inaccessible_lead')}
                  </p>
                  <p className="text-xs text-amber-700 pt-1 font-medium">
                    {t('whatsapp_phone_numbers.setup.waba_inaccessible_action_label')}
                  </p>
                  <ul className="space-y-1 ml-5.5">
                    <li className="text-xs text-amber-700 list-disc">
                      {t('whatsapp_phone_numbers.setup.waba_inaccessible_action_step1')}
                    </li>
                    <li className="text-xs text-amber-700 list-disc">
                      {t('whatsapp_phone_numbers.setup.waba_inaccessible_action_step2')}
                    </li>
                  </ul>
                  <div className="pt-2 flex items-center gap-3">
                    <Button
                      onClick={() => handleStep1Click()}
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      data-testid="waba-inaccessible-reconnect-button"
                    >
                      {t('whatsapp_phone_numbers.setup.waba_inaccessible_reconnect_button')}
                    </Button>
                    <Button
                      onClick={() => setDisconnectDialogOpen(true)}
                      size="sm"
                      variant="link"
                      className="text-amber-700 hover:text-amber-900 px-0"
                      data-testid="waba-inaccessible-remove-button"
                    >
                      {t('whatsapp_phone_numbers.setup.start_over_button')}
                    </Button>
                  </div>
                </div>
              )}

              {integrationStatus === 'setup_incomplete' && (
                <div
                  className="rounded-xl border border-amber-200 bg-amber-50 p-3 space-y-2"
                  data-testid="setup-incomplete-banner"
                >
                  <div className="flex items-center gap-1.5">
                    <AlertTriangle className="size-4 text-amber-600 shrink-0" />
                    <span className="text-sm font-medium text-amber-800">
                      {t('whatsapp_phone_numbers.setup.setup_incomplete_title')}
                    </span>
                  </div>
                  <p className="text-xs text-amber-800">
                    {t('whatsapp_phone_numbers.setup.setup_incomplete_lead')}
                  </p>
                  <p className="text-xs text-amber-700 pt-1 font-medium">
                    {t('whatsapp_phone_numbers.setup.setup_incomplete_action_label')}
                  </p>
                  <ul className="space-y-1 ml-5.5">
                    <li className="text-xs text-amber-700 list-disc">
                      {t('whatsapp_phone_numbers.setup.setup_incomplete_action_step1')}
                    </li>
                    <li className="text-xs text-amber-700 list-disc">
                      {t('whatsapp_phone_numbers.setup.setup_incomplete_action_step2')}
                    </li>
                  </ul>
                  <div className="pt-2 flex items-center gap-3">
                    <Button
                      onClick={() => handleStep1Click()}
                      size="sm"
                      className="bg-green-500 hover:bg-green-600 text-white"
                      data-testid="setup-incomplete-reconnect-button"
                    >
                      {t('whatsapp_phone_numbers.setup.setup_incomplete_reconnect_button')}
                    </Button>
                    <Button
                      onClick={() => setDisconnectDialogOpen(true)}
                      size="sm"
                      variant="link"
                      className="text-amber-700 hover:text-amber-900 px-0"
                      data-testid="setup-incomplete-remove-button"
                    >
                      {t('whatsapp_phone_numbers.setup.start_over_button')}
                    </Button>
                  </div>
                </div>
              )}

              {isSetupInProgress && integrationStatus !== 'connected' && (
                <div
                  className="rounded-xl border border-blue-200 bg-blue-50 p-3 space-y-1.5"
                  data-testid="setup-in-progress-banner"
                >
                  <div className="flex items-center gap-1.5">
                    <Loader2 className="size-4 text-blue-600 shrink-0 animate-spin" />
                    <span className="text-sm font-medium text-blue-800">
                      {t('whatsapp_phone_numbers.setup.in_progress_title')}
                    </span>
                  </div>
                  <p className="text-xs text-blue-700">
                    {t('whatsapp_phone_numbers.setup.in_progress_description')}
                  </p>
                </div>
              )}

              <div className="space-y-3 pt-2">
                <div
                  className="flex items-center gap-3 rounded-xl bg-accent p-3"
                  data-testid="step1-card"
                >
                  <StepIndicator completed={step1Complete} stepNumber={1} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {t('whatsapp_phone_numbers.setup.step1_title')}
                    </p>
                    <p className="text-xs text-foreground-subtle mt-0.5">
                      {t('whatsapp_phone_numbers.setup.step1_description')}
                    </p>
                  </div>
                  <Button
                    onClick={() => handleStep1Click()}
                    variant={step1Complete ? 'outline' : 'default'}
                    size="sm"
                    className={step1Complete ? '' : 'bg-green-500 hover:bg-green-600 text-white'}
                    disabled={isSetupInProgress}
                    data-testid="whatsapp-setup-step1"
                  >
                    {step1Complete
                      ? t('whatsapp_phone_numbers.setup.redo')
                      : t('whatsapp_phone_numbers.setup.step1')}
                  </Button>
                </div>

                <div
                  className="flex items-center gap-3 rounded-xl bg-accent p-3"
                  data-testid="step2-card"
                >
                  <StepIndicator completed={step2Complete} stepNumber={2} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground">
                      {t('whatsapp_phone_numbers.setup.step2_title')}
                    </p>
                    <p className="text-xs text-foreground-subtle mt-0.5">
                      {t('whatsapp_phone_numbers.setup.step2_description')}
                    </p>
                  </div>
                  <Button
                    onClick={handleStep2Click}
                    variant={step2Complete ? 'outline' : 'default'}
                    size="sm"
                    className={step2Complete ? '' : 'bg-green-500 hover:bg-green-600 text-white'}
                    disabled={isSetupInProgress}
                    data-testid="whatsapp-setup-step2"
                  >
                    {step2Complete
                      ? t('whatsapp_phone_numbers.setup.redo')
                      : t('whatsapp_phone_numbers.setup.step2')}
                  </Button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshStatus}
                  disabled={statusFetching}
                  data-testid="refresh-status-button"
                >
                  <RefreshCw
                    className={`size-3.5 mr-1.5 ${statusFetching ? 'animate-spin' : ''}`}
                  />
                  {t('whatsapp_phone_numbers.setup.refresh_status')}
                </Button>
              </div>

              {statusData &&
                integrationStatus !== 'connected' &&
                integrationStatus !== 'waba_inaccessible' &&
                integrationStatus !== 'setup_incomplete' && (
                  <IssuesBanner issues={statusData.issues} />
                )}

              <div className="flex items-center gap-2 text-xs text-foreground-subtle pt-1">
                <HelpCircle className="size-3.5 shrink-0" />
                <p>
                  {t('whatsapp_phone_numbers.setup.help')}{' '}
                  <a
                    href={CONTACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="underline font-medium hover:text-foreground"
                    data-testid="whatsapp-setup-contact"
                  >
                    {t('whatsapp_phone_numbers.setup.contact_support')}
                  </a>
                </p>
              </div>
            </div>
          )}

          {integrationStatus === 'connected' && (
            <>
              <Separator />

              {isLoading ? (
                <div className="space-y-4" data-testid="connected-skeleton">
                  <div className="rounded-xl bg-muted animate-pulse h-24" />
                  <div className="rounded-xl bg-muted animate-pulse h-20" />
                  <div className="rounded-xl bg-muted animate-pulse h-20" />
                </div>
              ) : (
                <>
                  {integrationInfo && (
                    <div
                      className="rounded-xl bg-muted p-4 space-y-3"
                      data-testid="integration-info-banner"
                    >
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-foreground-subtle font-normal">
                            {t('whatsapp_phone_numbers.info.whatsapp_account')}
                          </Label>
                          <p
                            className="text-sm font-mono mt-0.5"
                            data-testid="whatsapp-account-label"
                          >
                            {integrationInfo.whatsapp_id}
                          </p>
                        </div>
                        <div>
                          <Label className="text-xs text-foreground-subtle font-normal">
                            {t('whatsapp_phone_numbers.info.connected_since')}
                          </Label>
                          <p className="text-sm mt-0.5" data-testid="connected-since">
                            {formatDate(integrationInfo.created_timestamp)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center">
                        <TooltipProvider>
                          <div className="flex items-center gap-6">
                            <div className="flex items-center gap-1.5" data-testid="webhook-status">
                              <span
                                className={`inline-block size-2.5 rounded-full ${integrationInfo.webhook_subscribed ? 'bg-green-500' : 'bg-red-500'}`}
                              />
                              <span className="text-sm text-muted-foreground">
                                {integrationInfo.webhook_subscribed
                                  ? t('whatsapp_phone_numbers.info.webhook_active')
                                  : t('whatsapp_phone_numbers.info.webhook_inactive')}
                              </span>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="size-3.5 text-foreground-subtle cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t('whatsapp_phone_numbers.info.webhook_tooltip')}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                            <div className="flex items-center gap-1.5" data-testid="admin-status">
                              <span
                                className={`inline-block size-2.5 rounded-full ${integrationInfo.admin_assigned ? 'bg-green-500' : 'bg-red-500'}`}
                              />
                              <span className="text-sm text-muted-foreground">
                                {integrationInfo.admin_assigned
                                  ? t('whatsapp_phone_numbers.info.admin_assigned')
                                  : t('whatsapp_phone_numbers.info.admin_not_assigned')}
                              </span>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Info className="size-3.5 text-foreground-subtle cursor-help" />
                                </TooltipTrigger>
                                <TooltipContent>
                                  {t('whatsapp_phone_numbers.info.admin_tooltip')}
                                </TooltipContent>
                              </Tooltip>
                            </div>
                          </div>
                        </TooltipProvider>
                      </div>

                      {statusData && statusData.issues.length > 0 && (
                        <IssuesBanner issues={statusData.issues} />
                      )}
                    </div>
                  )}

                  <div className="space-y-3">
                    <div>
                      <h3 className="text-base font-semibold text-foreground">
                        {t('whatsapp_phone_numbers.phone_numbers_section.title')}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        {t('whatsapp_phone_numbers.description')}
                      </p>
                    </div>

                    {phoneNumbers.length === 0 && (
                      <div
                        className="text-sm text-foreground-subtle py-4"
                        data-testid="empty-state"
                      >
                        {t('whatsapp_phone_numbers.empty')}
                      </div>
                    )}

                    {phoneNumbers.map((entry) => (
                      <div
                        key={entry.phone_number_id}
                        className="rounded-xl border border-border py-4 px-5 bg-card"
                        data-testid={`phone-number-row-${entry.phone_number_id}`}
                      >
                        <div className="grid grid-cols-[230px_1fr_60px_50px] items-center gap-x-8 gap-y-4">
                          <div className="min-w-0">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t('whatsapp_phone_numbers.card.phone_number')}
                            </Label>
                            <p className="text-sm font-mono mt-0.5 truncate">
                              {entry.display_phone_number}
                            </p>
                          </div>
                          <div className="min-w-0">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t('whatsapp_phone_numbers.card.verified_name')}
                            </Label>
                            <p
                              className="text-sm mt-0.5 truncate"
                              data-testid={`verified-name-${entry.phone_number_id}`}
                            >
                              {entry.verified_name}
                            </p>
                          </div>
                          <div className="flex flex-col items-center">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {t('whatsapp_phone_numbers.card.quality')}
                            </Label>
                            <div className="mt-0.5">
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span>
                                      <Badge
                                        variant={
                                          QUALITY_BADGE_VARIANT[entry.quality_rating] ?? 'secondary'
                                        }
                                        className="text-xs cursor-default"
                                        data-testid={`quality-rating-${entry.phone_number_id}`}
                                      >
                                        {t(
                                          QUALITY_DISPLAY_KEY[entry.quality_rating] ??
                                            'whatsapp_phone_numbers.card.quality_unknown',
                                        )}
                                      </Badge>
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    {t(
                                      QUALITY_TOOLTIP_KEY[entry.quality_rating] ??
                                        'whatsapp_phone_numbers.card.quality_unknown_tooltip',
                                    )}
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            </div>
                          </div>
                          <div className="flex flex-col items-end gap-1">
                            <Label className="text-xs text-foreground-subtle font-normal">
                              {entry.is_active
                                ? t('whatsapp_phone_numbers.card.active')
                                : t('whatsapp_phone_numbers.card.inactive')}
                            </Label>
                            <div className="flex items-center gap-2">
                              {toggleMutation.isPending &&
                                toggleMutation.variables?.phone_number_id ===
                                  entry.phone_number_id && (
                                  <Loader2
                                    className="size-3.5 animate-spin text-foreground-subtle"
                                    data-testid={`toggle-loader-${entry.phone_number_id}`}
                                  />
                                )}
                              <Switch
                                checked={entry.is_active}
                                onCheckedChange={(checked) => handleToggle(entry, checked)}
                                disabled={toggleMutation.isPending}
                                size="sm"
                                data-testid={`toggle-${entry.phone_number_id}`}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}

                    <div className="pt-4">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => setDisconnectDialogOpen(true)}
                        data-testid="disconnect-button"
                      >
                        <Trash2 className="size-3.5 mr-1.5" />
                        {t('whatsapp_phone_numbers.disconnect.button')}
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <DialogPrimitive.Root open={disconnectDialogOpen} onOpenChange={setDisconnectDialogOpen}>
          <DialogPrimitive.Overlay className="!mt-0 absolute inset-0 bg-black/30 z-20" />
          <DialogPrimitive.Content
            className="absolute inset-0 flex items-center justify-center p-4 z-30 focus:outline-none"
            role="alertdialog"
            aria-modal="true"
            data-testid="disconnect-dialog"
          >
            <div className="space-y-4 bg-card rounded-lg p-6 max-w-md w-full shadow-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>
                  <Icon
                    icon="mingcute:alert-fill"
                    className="inline bg-red-100 rounded-lg text-red-500 p-2 box-content"
                  />
                  {disconnectDialogCopy.title}
                </AlertDialogTitle>
              </AlertDialogHeader>
              <AlertDialogDescription>{disconnectDialogCopy.description}</AlertDialogDescription>
              <Separator className="my-7" />
              <AlertDialogFooter>
                <AlertDialogCancel asChild>
                  <Button size="md" variant="secondary">
                    {t('whatsapp_phone_numbers.disconnect.cancel')}
                  </Button>
                </AlertDialogCancel>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => deleteMutation.mutate()}
                  disabled={deleteMutation.isPending}
                  data-testid="disconnect-confirm-button"
                >
                  {deleteMutation.isPending && <Loader2 className="size-4 mr-1.5 animate-spin" />}
                  {disconnectDialogCopy.confirm}
                </Button>
              </AlertDialogFooter>
            </div>
          </DialogPrimitive.Content>
        </DialogPrimitive.Root>
      </SheetContent>
    </Sheet>
  );
};
